import { create } from 'zustand';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  duration: number;
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

// `crypto.randomUUID()` is only defined in secure contexts (HTTPS or
// localhost). The cloud UI is sometimes accessed over plain HTTP from a
// LAN IP during early dev — there `crypto` is undefined and the toast
// push throws, breaking any user action that surfaces a toast on success
// (delete, save, logout, …). This fallback is good-enough uniqueness
// for short-lived UI IDs: 36^15 ≈ 2^77 keyspace, far more than the
// number of toasts a tab will ever have alive at once.
let _toastSeq = 0;
const makeToastId = (): string => {
  // Web Crypto methods are bound to the `crypto` host object — extracting
  // `randomUUID` into a local and calling it bare throws "Illegal
  // invocation". Call it as a property access so the receiver is preserved.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  _toastSeq += 1;
  return `t-${Date.now().toString(36)}-${_toastSeq}-${Math.random().toString(36).slice(2, 9)}`;
};

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = makeToastId();
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    if (t.duration > 0) {
      setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
      }, t.duration);
    }
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}));
